/**
 * Core patient interface based on OpenAPI specification
 * Represents the data structure returned by the backend API
 */
export interface IPatient {
    entityId: string;                    // Required UUID
    zTouchTimestamp?: string;            // Legacy modification timestamp (write-only)
    zCreationTimestamp?: string;         // Legacy creation timestamp (write-only) 
    firstName?: string;                  // Patient first name
    lastName: string;                    // Patient last name (required)
    gender: PatientGender;              // Patient gender (required)
    birthDate: string;                  // Birth date in ISO format (required)
    birthPlace?: string;                // Birth place
    address?: string;                   // Patient address
    city?: string | null;               // Optional - filled from "more information"
    country?: string | null;            // Optional - filled from "more information"
    phone1?: string | null;             // Optional - the emergency contact is the number used
    phone2?: string;                    // Secondary phone
    email?: string;                     // Email address
    occupation?: string;                // Patient occupation
    emergencyContactName: string;       // Emergency contact name (required)
    emergencyContactPhone: string;      // Emergency contact phone (required)
    emergencyContactEmail?: string;     // Emergency contact email
    ztouchTimestamp: string;            // Normalized modification timestamp
    zcreationTimestamp: string;         // Creation timestamp
}

/**
 * Input DTO for patient creation/update operations
 * Based on PatientIn from OpenAPI spec
 */
export interface IPatientIn {
    lastName: string;                   // Required
    firstName?: string;
    gender: PatientGender;             // Required
    phone1?: string | null;
    phone2?: string;
    birthDate: string;                 // Required - ISO date format
    birthPlace?: string;
    email?: string;
    occupation?: string;
    address?: string;
    emergencyContactName: string;      // Required
    emergencyContactPhone: string;     // Required
    emergencyContactEmail?: string;
    city?: string | null;
    country?: string | null;
}

/**
 * Patient gender enumeration from OpenAPI spec
 */
export const PatientGenders = ['MALE', 'FEMALE', 'OTHER'] as const;
export type PatientGender = typeof PatientGenders[number];

/**
 * French translations for patient genders
 */
export const GenderTranslations: { [key in PatientGender]: string } = {
    'MALE': 'Masculin',
    'FEMALE': 'Féminin',
    'OTHER': 'Autre'
};

/**
 * Get French translation for a gender
 */
export function translateGender(gender: PatientGender): string {
    return GenderTranslations[gender] || gender;
}

/**
 * Patient filter interface for search and filtering
 */
export interface IPatientFilter {
    search?: string;                   // Search term for name/phone/email
    f_gender?: PatientGender;         // Filter by gender
    sort?: string;                    // Sort field and direction
    page?: number;                    // Page number (default: 1)
    per_page?: number;               // Items per page (default: 20)
}

/**
 * Sort object from Spring Data
 */
export interface ISortObject {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
}

/**
 * Pageable object from Spring Data
 */
export interface IPageableObject {
    offset: number;
    sort: ISortObject;
    pageSize: number;
    pageNumber: number;
    paged: boolean;
    unpaged: boolean;
}

/**
 * Spring Data Page response structure
 */
export interface IPagePatientOut {
    totalElements: number;             // Total number of items across all pages
    totalPages: number;               // Total number of pages
    size: number;                     // Page size (items per page)
    content: IPatient[];              // Patient data array for current page
    number: number;                   // Current page number (0-based)
    sort: ISortObject;               // Sort information
    first: boolean;                   // Is this the first page?
    last: boolean;                    // Is this the last page?
    numberOfElements: number;         // Number of elements in current page
    pageable: IPageableObject;       // Pageable configuration
    empty: boolean;                   // Is the page empty?
}

/**
 * Converted pagination response for easier frontend use
 */
export interface IPatientPagination {
    currentPage: number;              // 1-based current page number
    pageSize: number;                // Items per page
    totalItems: number;              // Total number of items
    totalPages: number;              // Total number of pages
    hasNext: boolean;                // Has next page
    hasPrevious: boolean;            // Has previous page
    isFirst: boolean;                // Is first page
    isLast: boolean;                 // Is last page
    numberOfElements: number;        // Elements in current page
}

/**
 * Patient list response interface
 */
export interface IPatientListResponse extends IPagePatientOut {
    // Extends the Spring Data Page structure directly
}

/**
 * Patient creation request interface
 */
export interface IPatientCreateRequest extends IPatientIn {
    // Inherits all fields from IPatientIn
}

/**
 * Patient update request interface  
 */
export interface IPatientUpdateRequest extends IPatientIn {
    // Inherits all fields from IPatientIn
}

/**
 * Patient search result interface for display
 */
export interface IPatientSearchResult {
    patients: IPatient[];             // Found patients
    totalCount: number;               // Total count for pagination
    hasMore: boolean;                 // More results available
}

/**
 * Patient validation interface
 */
export interface IPatientValidation {
    isValid: boolean;                 // Overall validation status
    errors: { [key: string]: string }; // Field-specific errors
}

/**
 * Patient statistics interface
 */
export interface IPatientStats {
    totalPatients: number;            // Total number of patients
    maleCount: number;               // Number of male patients
    femaleCount: number;             // Number of female patients
    otherCount: number;              // Number of other gender patients
    averageAge: number;              // Average age of patients
}

/**
 * Patient age group interface
 */
export interface IPatientAgeGroup {
    group: string;                   // Age group name (e.g., "18-25")
    count: number;                   // Number of patients in group
    percentage: number;              // Percentage of total
}

/**
 * Helper function to convert Spring Data Page to frontend pagination
 */
export function convertPageToPagination(page: IPagePatientOut): IPatientPagination {
    return {
        currentPage: page.number + 1,        // Convert from 0-based to 1-based
        pageSize: page.size,
        totalItems: page.totalElements,
        totalPages: page.totalPages,
        hasNext: !page.last,
        hasPrevious: !page.first,
        isFirst: page.first,
        isLast: page.last,
        numberOfElements: page.numberOfElements
    };
}